import { Auth } from "./auth";
import { API } from "./api";
import { Storage } from "./storage";
import { v1 as uuid } from "uuid";

interface ICredentials {
  accessKeyId: string;
  secretAccessKey: string;
  sessionToken?: string;
}

interface ISignUpResult {
  user: any;
  userConfirmed: boolean;
  userSub: string;
}

const apiName = "api";

interface Result {
  success: boolean;
  message?: string;
}

interface SignUpResult extends Result {
  user?: ISignUpResult;
}

interface SignUpAttributes {
  firstName?: string;
  lastName?: string;
  institute: string;
  fieldOfResearch: string;
  geoLocationOfResearch: string;
  phoneNumber: string;
  editLocked: boolean;
  visualizationApproval: boolean;
}

const userSignUp = async (
  email: string,
  password: string,
  attributes: SignUpAttributes
): Promise<SignUpResult> => {
  const result: SignUpResult = {
    success: false,
  };

  const {
    firstName,
    lastName,
    institute,
    fieldOfResearch,
    geoLocationOfResearch,
    phoneNumber,
    editLocked,
    visualizationApproval
  } = attributes;

  const params = {
    username: email,
    password,
    attributes: {
      email,
      given_name: firstName,
      family_name: lastName,
      phone_number: phoneNumber,
      institute: institute,
      research_field: fieldOfResearch,
      research_geo: geoLocationOfResearch,
      edit_locked: editLocked,
      dv_approval_required: visualizationApproval
    },
  };

  try {
    const user = await Auth.signUp(params);
    result.success = true;
    result.user = user;
  } catch(error: unknown) {

    if (error instanceof SyntaxError) {
      result.message  = error.stack;
    }
  }

  return result;
};

interface SignInResult extends Result {
  newPasswordRequired: boolean;
  email?: string;
  username?: string;
  user?: any;
}

const userSignIn = async (
  email: string,
  password: string,
  newPassword?: string
): Promise<SignInResult> => {
  const result: SignInResult = {
    success: false,
    newPasswordRequired: false,
  };

  try {
    const user = await Auth.signIn(email, password);

    if (user.challengeName === "NEW_PASSWORD_REQUIRED") {
      result.newPasswordRequired = true;

      if (newPassword) {
        await Auth.completeNewPassword(user, newPassword, { email });
        result.success = true;
        result.username = user.username;
        result.message = "New password saved";
        result.email = email;
      } else {
        result.message = "Please set a new password";
      }
    } else {
      result.success = true;
      result.username = user.username;
      result.email = email;
    }

    result.user = user;
  } catch(error: unknown) {

    if (error instanceof SyntaxError) {
      result.message  = error.stack;
    }
  }


  return result;
};

interface ConfirmationResult extends Result {
  email?: string;
  username?: string;
}

const forgotPassword = async (
  email: string
): Promise<ConfirmationResult> => {
  const result: ConfirmationResult = {
    success: false,
  };

  try {
    const data = await Auth.forgotPassword(email);

    result.success = true;
    result.username = email;
    result.email = email;
    result.message = "Code successfully sent to the registered email.";
  } catch(error: unknown) {

    if (error instanceof SyntaxError) {
      result.message  = error.stack;
    }
  }

  return result;
};

const forgotPasswordConfirm = async (
  email: string,
  code: string,
  password: string
): Promise<ConfirmationResult> => {
  const result: ConfirmationResult = {
    success: false,
  };

  try {
    const data = await Auth.forgotPasswordSubmit(email, code, password);

    result.success = true;
    result.username = email;
    result.email = email;
    result.message = "Password updated successfully.";
  } catch(error: unknown) {

    if (error instanceof SyntaxError) {
      result.message  = error.stack;
    }
  }


  return result;
};

const confirmEmail = async (
  email: string,
  code: string
): Promise<ConfirmationResult> => {
  const result: ConfirmationResult = {
    success: false,
  };

  try {
    const data = await Auth.confirmSignUp(email, code);

    result.success = true;
    result.username = email;
    result.email = email;
    result.message = "User confirmed successfully.";
  } catch(error: unknown) {

    if (error instanceof SyntaxError) {
      result.message  = error.stack;
    }
  }


  return result;
};

const usernameExistCheck = (email: string) => {
  let result = undefined;
  try {
    result = Auth.signIn(email, "_");
  } catch (e) {
  }
  return result;
};

const userSignOut = async (): Promise<void> => {
  try {
    await Auth.signOut();
  } catch(error: unknown) {

    if (error instanceof SyntaxError) {
    }
  }
};

interface SessionResult extends Result {
  id?: number;
  username?: string;
  email?: string;
  is_staff?: boolean;
  is_superuser?: boolean;

  phone_number?: string;
  fname?: string;
  lname?: string;
  institute?: string;
  research_field?: string;
  research_geo?: string;
  contributor_access?: boolean | string;
  terms_version_acknowledged?: string;
  privacy_version_acknowledged?: string;
  latest_terms_version?: string;
  latest_privacy_version?: string;
}

const getUserSession = async (): Promise<SessionResult> => {
  const result: SessionResult = {
    success: false,
  };

  try {
    const user = await Auth.currentSession();
    const idToken = user.getIdToken();
    result.success = true;
    result.id = idToken.payload.id;
    result.username = idToken.payload.username;
    result.is_staff = idToken.payload.is_staff;
    result.is_superuser = idToken.payload.is_superuser;
    result.email = idToken.payload.email;
    result.phone_number = idToken.payload.phone_number;
    result.fname = idToken.payload.given_name;
    result.lname = idToken.payload.family_name;
    result.institute = idToken.payload.institute;
    result.research_field = idToken.payload.research_field;
    result.research_geo = idToken.payload.research_geo;
    result.contributor_access = idToken.payload.contributor_access;
    result.terms_version_acknowledged = idToken.payload.terms_version_acknowledged;
    result.privacy_version_acknowledged = idToken.payload.privacy_version_acknowledged;
    result.latest_terms_version = idToken.payload.latest_terms_version;
    result.latest_privacy_version = idToken.payload.latest_privacy_version;
  } catch(error: unknown) {
    if (error instanceof SyntaxError) {
      result.message = error.stack;
    }
  }

  return result;
};

const changePassword = async (
  oldPassword: string,
  newPassword: string
): Promise<Result> => {
  const result: Result = {
    success: false,
  };

  try {
    const user = await Auth.currentAuthenticatedUser();
    await Auth.changePassword(user, oldPassword, newPassword);
    result.success = true;
  } catch(error: unknown) {

    if (error instanceof SyntaxError) {
      result.message  = error.stack;
    }
  }

  return result;
};

const resetPassword = async (
  email: string,
  code?: string,
  newPassword?: string
): Promise<Result> => {
  const result: Result = {
    success: false,
  };

  try {
    if (code && newPassword) {
      await Auth.forgotPasswordSubmit(email, code, newPassword);
      result.success = true;
      result.message = "New password saved";
    } else {
      await Auth.forgotPassword(email);
      result.success = true;
      result.message = "A code has been sent to your email address";
    }
  } catch(error: unknown) {

    if (error instanceof SyntaxError) {
      result.message  = error.stack;
    }
  }


  return result;
};

const verifyEmail = async (code?: string): Promise<Result> => {
  const result: Result = {
    success: false,
  };

  try {
    if (code) {
      await Auth.verifyCurrentUserAttributeSubmit("email", code);
      result.success = true;
      result.message = "Your email address has been verified";
    } else {
      await Auth.verifyCurrentUserAttribute("email");
      result.success = true;
      result.message = "A code has been sent to your email address";
    }
  } catch(error: unknown) {

    if (error instanceof SyntaxError) {
      result.message  = error.stack;
    }
  }


  return result;
};

type Method = "get" | "post" | "put" | "del" | "patch" | "head";

interface QueryParams {
  queryStringParameters?: object;
  body?: object;
}

const invokeAPI = async (
  method: Method,
  path: string,
  params: object = {}
): Promise<any> => {
  const queryParams: QueryParams = {};

  if (method === "get" || method === "del") {
    queryParams.queryStringParameters = params;
  } else {
    queryParams.body = params;
  }

  return await API[method](apiName, path, queryParams);
};

type AccessLevel = "private" | "protected" | "public";

const s3Upload = async (
  file: any,
  setProgress: any,
  setProjectUploadFail: any,
): Promise<string> => {
  const fileKey = `${uuid()}/${file.name}`;
  const params = {
    useAccelerateEndpoint: true,
    contentType: file.type,
    progressCallback: (progress: object) => {
      setProgress(progress);
  },
  };

  try {
    const res = await Storage.put(`${fileKey}`, file, params);
    // Storage.put returns an object { key: serverKey }
    if (res && (res as any).key) {
      return (res as any).key;
    }
    return fileKey;
  } catch(error: unknown) {
    setProjectUploadFail(true);
    return "";
}
};

const s3Get = async (
  key: string,
  accessLevel: AccessLevel = "public",
  removeSignature: boolean = false
): Promise<string> => {
  const params = {
    level: accessLevel,
  };

  try {
    const url = await Storage.get(key, params);
    if (removeSignature) {
      const arr = (url as string).split("?");
      return arr[0];
    } else {
      return url as string;
    }
  } catch(error: unknown) {

    if (error instanceof SyntaxError) {
      return "";
    }
  }
  return "";
  };

const s3Remove = async (
  key: string,
  accessLevel: AccessLevel = "public"
): Promise<any> => {
  const params = {
    level: accessLevel,
  };

  try {
    Storage.configure(params);
    return await Storage.remove(key);
  } catch(error: unknown) {

    if (error instanceof SyntaxError) {
      return "";
    }
  }
  };

const signUrl = async (url: string): Promise<string> => {
  return url;
};

export {
  userSignUp,
  userSignIn,
  userSignOut,
  getUserSession,
  resetPassword,
  changePassword,
  verifyEmail,
  invokeAPI,
  s3Upload,
  s3Get,
  s3Remove,
  signUrl,
  usernameExistCheck,
  forgotPassword,
  confirmEmail,
  forgotPasswordConfirm,
};

